import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, RefreshCw, Users, Package, AlertTriangle, TrendingUp } from "lucide-react";
import NotificationStatusChart from "@/components/dashboard/charts/NotificationStatusChart";
import NotificationVolumeChart from "@/components/dashboard/charts/NotificationVolumeChart";
import PackagePickupTrendsChart from "@/components/dashboard/charts/PackagePickupTrendsChart";
import InventoryAlertsChart from "@/components/dashboard/charts/InventoryAlertsChart";
import UserActivityChart from "@/components/dashboard/charts/UserActivityChart";
import ResponseTimeChart from "@/components/dashboard/charts/ResponseTimeChart";

const LiveDashboard = () => {
  const [refreshInterval, setRefreshInterval] = useState("30");
  const [timeRange, setTimeRange] = useState("24h");
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const handleManualRefresh = () => {
    setLastUpdate(new Date());
  };

  const formatLastUpdate = (date: Date) => {
    return date.toLocaleTimeString('nl-NL', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Live Dashboard</h1>
          <p className="text-muted-foreground">
            Real-time overzicht van alle operationele data
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Activity className="w-4 h-4 text-green-500 animate-pulse" />
            <span>Laatste update: {formatLastUpdate(lastUpdate)}</span>
          </div>
          
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">1 uur</SelectItem>
              <SelectItem value="4h">4 uur</SelectItem>
              <SelectItem value="24h">24 uur</SelectItem>
              <SelectItem value="7d">7 dagen</SelectItem>
              <SelectItem value="30d">30 dagen</SelectItem>
            </SelectContent>
          </Select>

          <Select value={refreshInterval} onValueChange={setRefreshInterval}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 sec</SelectItem>
              <SelectItem value="10">10 sec</SelectItem>
              <SelectItem value="30">30 sec</SelectItem>
              <SelectItem value="60">1 min</SelectItem>
              <SelectItem value="300">5 min</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={handleManualRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Ververs
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actieve Meldingen</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">23</div>
            <p className="text-xs text-muted-foreground">
              +2 sinds vorig uur
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actieve Gebruikers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">8</div>
            <p className="text-xs text-muted-foreground">
              Online nu
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ophaalcontainers</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">45</div>
            <p className="text-xs text-muted-foreground">
              Vandaag opgehaald
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Voorraad Alerts</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">7</div>
            <p className="text-xs text-muted-foreground">
              Lage voorraad items
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notification Analytics */}
        <NotificationStatusChart timeRange={timeRange} refreshInterval={parseInt(refreshInterval)} />
        <NotificationVolumeChart timeRange={timeRange} refreshInterval={parseInt(refreshInterval)} />
        
        {/* Operational Metrics */}
        <PackagePickupTrendsChart timeRange={timeRange} refreshInterval={parseInt(refreshInterval)} />
        <InventoryAlertsChart timeRange={timeRange} refreshInterval={parseInt(refreshInterval)} />
        
        {/* User Activity & Performance */}
        <UserActivityChart timeRange={timeRange} refreshInterval={parseInt(refreshInterval)} />
        <ResponseTimeChart timeRange={timeRange} refreshInterval={parseInt(refreshInterval)} />
      </div>

      {/* Status Indicators */}
      <Card>
        <CardHeader>
          <CardTitle>Systeem Status</CardTitle>
          <CardDescription>Real-time status van alle systemen</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm">Database</span>
              <Badge variant="secondary">Online</Badge>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm">Real-time Updates</span>
              <Badge variant="secondary">Actief</Badge>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm">Notifications</span>
              <Badge variant="secondary">Operationeel</Badge>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm">Inventory Sync</span>
              <Badge variant="secondary">Synchronized</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveDashboard;